'use client';

import { use, useMemo } from 'react';
import MenuItem from '@pixum/menu-item';
import type { PickupPointLocation } from 'services';
import { usePickupPointKeyboardNavigation } from '../hooks';
import { doesPickupPointMatchQuery, getPickupPointOptionId, getPickupPointText, getPickupPoints } from '../utils';
import type { PickupPointOptionsListProps } from './types';
import styles from '../../../CartAddressStep.module.css';

const NO_LOCATIONS: PickupPointLocation[] = [];

/** Loads pickup points for the current search and renders them filtered by the user's search query. */
export function PickupPointOptionsList({
  city,
  constants,
  onChange,
  query,
  selectedStation,
  zip
}: PickupPointOptionsListProps) {
  const result = use(getPickupPoints({ city, zip }));
  const locations = result.success ? result.data : NO_LOCATIONS;
  const filteredLocations = useMemo(
    () => locations.filter(location => doesPickupPointMatchQuery(location, query)),
    [locations, query]
  );

  const handleSelect = (location: PickupPointLocation) =>
    onChange({
      city: location.city,
      stationNumber: location.serviceId,
      streetAddOn: getPickupPointText(location),
      zip: location.zip
    });

  const activeLocation = usePickupPointKeyboardNavigation({
    locations: filteredLocations,
    selectedStation,
    onSelect: handleSelect
  });

  if (filteredLocations.length === 0) {
    return (
      <div className={styles.cartPickupPointNoResults} role="status">
        {constants['i18n_no_results_found']}
      </div>
    );
  }

  return (
    <>
      {filteredLocations.map(location => {
        const text = getPickupPointText(location);

        return (
          <MenuItem
            key={location.serviceId}
            aria-selected={text === selectedStation}
            className={styles.cartPickupPointOption}
            data-active={location === activeLocation}
            data-pickup-point-option="true"
            id={getPickupPointOptionId(location)}
            onClick={() => handleSelect(location)}
            disableKeyboardNav
            role="option"
            selected={text === selectedStation}
            subtext={`${location.zip} ${location.city}`}
            tag="li"
            tabIndex={-1}
            text={text}
          />
        );
      })}
    </>
  );
}
